import {
  ExtendedWarrantyCreateArgs,
  ExtendedWarrantyCreateSuccessResponse,
  GenericBindResponse,
  HouseholdCreateArgs,
  HouseholdCreateSuccessResponse,
  HouseholdDetailsResponse,
  HouseholdUpdateArgs,
  HouseholdUpdateSuccessResponse,
  GenericQuoteSuccessResponse,
  IDsData,
  GenericQuoteArgs,
} from '@sky-distribution/adapter-core-pc-rest-interfaces';
import { BuyRequest, BuyResponse } from '@sky-distribution/backend-gp-onboarding-interfaces';
import { getSessionId, logAndThrowHttpError } from '@sky-distribution/gp-onboarding-monitoring';
import { getAgentToken, isSalesSession, SALES_SESSION_HEADER_NAME } from '@sky-distribution/gp-onboarding-shared-utils';
import axios, { AxiosInstance } from 'axios';
import {
  API_BASE_URL, PathApi, PRODUCT_ID_HEADER_NAME, SESSION_ID_HEADER_NAME, TENANT_ID_HEADER_NAME,
} from '../config/config';
import {
  BindWithOTPRequest, DetailsResponse, RateArgs, RateResponse, Rating,
} from '../service/Policy.service.type';
import { retryWithRefreshedSalesToken } from './salesSessionInterceptor';

const api = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      [SESSION_ID_HEADER_NAME]: getSessionId(),
      ...(isSalesSession() && { [SALES_SESSION_HEADER_NAME]: 'true' }),
    },
  });

  instance.interceptors.response.use(undefined, retryWithRefreshedSalesToken(instance, getAgentToken()));

  return instance;
};

const setProductIdHeader = (productId: string): Record<string, string> => ({
  [PRODUCT_ID_HEADER_NAME]: productId,
});

const setHeaders = (productId: string, tenant?: string): Record<string, string> => ({
  [PRODUCT_ID_HEADER_NAME]: productId,
  ...(tenant && { [TENANT_ID_HEADER_NAME]: tenant }),
});

export const apiRate = async (
  body: RateArgs,
  productId: string,
): Promise<RateResponse> => {
  const { data } = await api()
    .post<Rating[]>(PathApi.RATE, body, { headers: { ...setProductIdHeader(productId) } })
    .catch(logAndThrowHttpError<Rating[]>('rate', body));

  return { ratings: data };
};

export const apiCreate = async (
  body: HouseholdCreateArgs | ExtendedWarrantyCreateArgs,
  productId: string,
  tenant?: string,
  context?: string,
): Promise<HouseholdCreateSuccessResponse> => {
  const bodyWithContext = context ? { ...body, context } : body;
  const { data } = await api()
    .post<HouseholdCreateSuccessResponse | ExtendedWarrantyCreateSuccessResponse>(PathApi.CREATE, bodyWithContext, {
    headers: { ...setHeaders(productId, tenant) },
  })
    .catch(
      logAndThrowHttpError<HouseholdCreateSuccessResponse>('create', bodyWithContext),
    );

  return data;
};

export const apiUpdate = async (
  body: HouseholdUpdateArgs,
  productId: string,
  context?: string,
): Promise<HouseholdUpdateSuccessResponse> => {
  const bodyWithContext = context ? { ...body, context } : body;
  const { data } = await api()
    .post<HouseholdUpdateSuccessResponse>(PathApi.UPDATE, bodyWithContext, { headers: { ...setProductIdHeader(productId) } })
    .catch(
      logAndThrowHttpError<HouseholdUpdateSuccessResponse>('update', bodyWithContext),
    );

  return data;
};

export const apiQuote = async (
  body: HouseholdUpdateArgs,
  productId: string,
  context?: string,
  tenant?: string,
): Promise<HouseholdUpdateSuccessResponse> => {
  const bodyWithContext = context ? { ...body, context } : body;
  const { data } = await api()
    .post<HouseholdUpdateSuccessResponse>(PathApi.QUOTE, bodyWithContext, {
      headers: { ...setHeaders(productId, tenant) },
    })
    .catch(logAndThrowHttpError<HouseholdUpdateSuccessResponse>('quote', bodyWithContext));

  return data;
};

export const apiQuoteRenewal = async (
  body: GenericQuoteArgs,
  productId: string,
): Promise<GenericQuoteSuccessResponse> => {
  const { data } = await api()
    .post<GenericQuoteSuccessResponse>(PathApi.QUOTE_RENEWAL, body, { headers: { ...setProductIdHeader(productId) } })
    .catch(logAndThrowHttpError<GenericQuoteSuccessResponse>('quote-renewal', body));

  return data;
};

export const apiBuy = async (
  body: BuyRequest,
  productId: string,
): Promise<BuyResponse> => {
  const { data } = await api()
    .post<BuyResponse>(PathApi.BUY, body, { headers: { ...setProductIdHeader(productId) } })
    .catch(logAndThrowHttpError<BuyResponse>('buy', body));

  return data;
};

export const apiBindWithOTP = async (
  body: BindWithOTPRequest,
  productId: string,
): Promise<GenericBindResponse> => {
  const { data } = await api()
    .post<GenericBindResponse>(PathApi.BIND_WITH_OTP, body, { headers: { ...setProductIdHeader(productId) } })
    .catch(logAndThrowHttpError<GenericBindResponse>('bindWithOTP', body));

  return data;
};

export const apiRetrieveSales = async (
  body: { ids: IDsData },
  productId: string,
): Promise<DetailsResponse> => {
  const { data } = await api()
    .post<HouseholdDetailsResponse>(PathApi.RETRIEVE_SALES, body, { headers: { ...setProductIdHeader(productId) } })
    .catch(logAndThrowHttpError<HouseholdDetailsResponse>('retrieveSales', body));

  return data;
};
